// visuals/theme.ts
// Centralized visual theme configuration for all effects
// Single source of truth for colors, intensities, durations, and visual parameters

export interface VisualTheme {
  palette: {
    player: string;
    enemy: string;
    asteroid: string;
    pellet: {
      low: string;
      mid: string;
      high: string;
    };
    bullet: {
      player: string;
      enemy: string;
    };
    explosion: {
      core: string;
      outer: string;
      smoke: string;
    };
    ui: {
      accent: string;
      danger: string;
      success: string;
    };
  };
  particles: {
    explosion: {
      flameColors: string[];
      smokeColors: string[];
      sparkColors: string[];
      debrisColors: string[];
      dustColors: string[];
      rockColors: string[];
    };
    debris: {
      minSize: number;
      maxSize: number;
      lifetime: number;
    };
    flame: {
      minSize: number;
      maxSize: number;
      lifetime: number;
      growthRate: number;
    };
    spark: {
      size: number;
      lifetime: number;
    };
  };
  postprocess: {
    bloom: {
      enabled: boolean;
      intensity: number;
      threshold: number;
      iterations: number;
    };
    chromaticAberration: {
      enabled: boolean;
      intensity: number;
    };
    scanlines: {
      enabled: boolean;
      intensity: number;
      count: number;
    };
    grain: {
      enabled: boolean;
      intensity: number;
    };
    vignette: {
      enabled: boolean;
      intensity: number;
      falloff: number;
    };
    colorGrade: {
      enabled: boolean;
      saturation: number;
      contrast: number;
      brightness: number;
    };
    screendoor: {
      enabled: boolean;
      intensity: number;
    };
    pixelation: {
      enabled: boolean;
      pixelSize: number;
    };
    palette: {
      enabled: boolean;
      ditherEnabled: boolean;
    };
  };
  screenEffects: {
    shakeDampening: number;
    flashDecayRate: number;
  };
  audio: {
    masterVolume: number;
    maxHearingRange: number;
    falloffExponent: number;
  };
}

export const GEOMETRY_WARS_THEME: VisualTheme = {
  palette: {
    player: "#42FF9E",
    enemy: "#FF4D6D",
    asteroid: "#8B7355",
    pellet: {
      low: "#64FF96",
      mid: "#78C9FF",
      high: "#C07CFF",
    },
    bullet: {
      player: "#00FFFF",
      enemy: "#FF0000",
    },
    explosion: {
      core: "#FFDD00",
      outer: "#FF9900",
      smoke: "#888888",
    },
    ui: {
      accent: "#42FF9E",
      danger: "#FF4D6D",
      success: "#64FF96",
    },
  },

  particles: {
    explosion: {
      flameColors: ["#FFDD00", "#FF9900", "#FF4400"],
      smokeColors: ["#888888", "#666666", "#444444", "#333333"],
      sparkColors: ["#FFAA00", "#FF8800"],
      debrisColors: ["#654321", "#8B7355", "#704214", "#5C4033"],
      dustColors: ["#8B7355", "#A0826D", "#696969", "#808080", "#778899"],
      rockColors: ["#654321", "#8B7355", "#704214", "#5C4033"],
    },
    debris: {
      minSize: 0.5,
      maxSize: 4.0,
      lifetime: 1500,
    },
    flame: {
      minSize: 0.2,
      maxSize: 3.0,
      lifetime: 600,
      growthRate: 4.0,
    },
    spark: {
      size: 0.6,
      lifetime: 400,
    },
  },

  postprocess: {
    bloom: {
      enabled: true,
      intensity: 1.0,
      threshold: 0.5,
      iterations: 5,
    },
    chromaticAberration: {
      enabled: true,
      intensity: 1.5,
    },
    scanlines: {
      enabled: true,
      intensity: 0.15,
      count: 800,
    },
    grain: {
      enabled: true,
      intensity: 0.015,
    },
    vignette: {
      enabled: true,
      intensity: 0.2,
      falloff: 0.5,
    },
    colorGrade: {
      enabled: false,
      saturation: 1.3,
      contrast: 1.15,
      brightness: 1.05,
    },
    screendoor: {
      enabled: false, // Disabled - screendoor is managed by GlobalOverlay in client.ts
      intensity: 0.3,
    },
    pixelation: {
      enabled: false,
      pixelSize: 2.0,
    },
    palette: {
      enabled: false,
      ditherEnabled: true,
    },
  },

  screenEffects: {
    shakeDampening: 0.85,
    flashDecayRate: 0.05,
  },

  audio: {
    masterVolume: 0.3,
    maxHearingRange: 800,
    falloffExponent: 2.5,
  },
};

let activeTheme: VisualTheme = GEOMETRY_WARS_THEME;

export function getTheme(): VisualTheme {
  return activeTheme;
}

export function setTheme(theme: VisualTheme): void {
  activeTheme = theme;
}

export function mergeTheme(partial: Partial<VisualTheme>): void {
  activeTheme = {
    palette: { ...activeTheme.palette, ...partial.palette },
    particles: { ...activeTheme.particles, ...partial.particles },
    postprocess: { ...activeTheme.postprocess, ...partial.postprocess },
    screenEffects: { ...activeTheme.screenEffects, ...partial.screenEffects },
    audio: { ...activeTheme.audio, ...partial.audio },
  };
}

export function resetTheme(): void {
  activeTheme = GEOMETRY_WARS_THEME;
}
